import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/terms.scss";

const termsSections = [
  ["terms-scope", "Scope and eligibility"],
  ["terms-products", "Products and pricing"],
  ["terms-orders", "Orders and payment"],
  ["terms-delivery", "Delivery and returns"],
  ["terms-use", "Using this site"],
  ["terms-liability", "Liability"],
  ["terms-law", "Law and changes"],
];

function Terms() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="policy-page terms">
      <header className="policy-page__hero">
        <div>
          <p className="policy-page__kicker">Legal / Store terms</p>
          <h1>Terms and conditions</h1>
        </div>
        <div className="policy-page__intro">
          <p>
            These terms govern use of this store and purchases made from{" "}
            {siteConfig.brandName}, operated by {siteConfig.legalName}. Please
            read them before ordering.
          </p>
          <span>Last updated / August 2026</span>
        </div>
      </header>

      <div className="policy-page__layout">
        <aside className="policy-page__aside">
          <p className="policy-page__kicker">On this page</p>
          <nav aria-label="Terms sections">
            {termsSections.map(([id, label]) => (
              <a href={`#${id}`} key={id}>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <section id="terms-scope">
            <p className="policy-page__section-number">01</p>
            <h2>Scope and eligibility</h2>
            <p>
              By using this site, you agree to these terms and our{" "}
              <Link to="/privacy" onClick={toTop}>
                privacy notice
              </Link>
              . You must have legal capacity to enter a purchase contract and
              must provide current, accurate checkout information. If ordering
              for an organisation, you confirm authority to bind it.
            </p>
            <p>
              These terms do not limit mandatory consumer rights in your place
              of residence. If a translated version conflicts with the English
              version, the interpretation permitted by applicable law will
              apply.
            </p>
          </section>

          <section id="terms-products">
            <p className="policy-page__section-number">02</p>
            <h2>Products, availability and pricing</h2>
            <p>
              We aim to describe colour, size, fabric and finish accurately.
              Screens render colour differently, and washed or natural materials
              may show intentional variation. Measurements are approximate and
              should be read with the product-specific fit notes.
            </p>
            <p>
              Products remain subject to availability. Placing an item in a bag
              does not reserve it. Prices are shown in the selected currency and
              may change before an order is submitted. Checkout identifies any
              delivery cost and taxes collected by us. Import duties not
              collected at checkout remain the recipient’s responsibility.
            </p>
            <p>
              If an obvious pricing or product-information error affects an
              order, we may contact you to confirm the correct terms or cancel
              and refund the affected item.
            </p>
          </section>

          <section id="terms-orders">
            <p className="policy-page__section-number">03</p>
            <h2>Orders, acceptance and payment</h2>
            <p>
              The checkout confirmation acknowledges receipt of your request; it
              does not by itself mean acceptance. A contract is formed when we
              send dispatch confirmation for an item. Different items may be
              accepted and dispatched separately.
            </p>
            <p>
              We may decline or cancel an order for stock, payment, suspected
              fraud, delivery restrictions, purchase limits or an evident error.
              Any amount captured for a cancelled item will be returned to the
              original payment method.
            </p>
            <p>
              You authorise the selected payment provider to validate and charge
              the order. Payment providers may perform security checks under
              their own terms. Do not submit an order using a payment method
              without the account holder’s permission.
            </p>
          </section>

          <section id="terms-delivery">
            <p className="policy-page__section-number">04</p>
            <h2>Delivery, cancellation and returns</h2>
            <p>
              Preparation and transit estimates are not guaranteed delivery
              dates. Risk passes when the parcel is delivered to you or a person
              you nominate; ownership passes once full payment is received.
              Review our{" "}
              <Link to="/shipping" onClick={toTop}>
                shipping information
              </Link>{" "}
              for current methods and estimates.
            </p>
            <p>
              Eligible consumers may notify us of a return within the period in
              our{" "}
              <Link to="/returns" onClick={toTop}>
                returns policy
              </Link>
              . Product-condition requirements and lawful exceptions apply.
              Nothing in these terms removes remedies for goods that are faulty,
              damaged or not as described.
            </p>
          </section>

          <section id="terms-use">
            <p className="policy-page__section-number">05</p>
            <h2>Using this site and intellectual property</h2>
            <p>
              You may use the site for personal shopping and information. You
              may not interfere with security, scrape the store at unreasonable
              scale, introduce malicious code, misuse another person’s identity,
              resell access, or use site content to create a confusingly similar
              service.
            </p>
            <p>
              Site design, copy, photography, graphics and marks are owned by or
              licensed to {siteConfig.brandName} and protected by applicable
              intellectual-property laws. A limited right to view the site does
              not transfer ownership or permit commercial reuse.
            </p>
            <p>
              Links to third-party services are supplied for convenience. We do
              not control their availability, content or separate terms.
            </p>
          </section>

          <section id="terms-liability">
            <p className="policy-page__section-number">06</p>
            <h2>Service availability and liability</h2>
            <p>
              We may maintain, update or suspend parts of the site. To the
              extent permitted by law, we are not responsible for losses that
              were not a foreseeable result of our breach, business losses from
              consumer use, or delay caused by events reasonably beyond our
              control.
            </p>
            <p>
              Nothing excludes liability that cannot legally be excluded,
              including liability for fraud or for death or personal injury
              caused by negligence. Your statutory rights and mandatory local
              remedies remain unaffected.
            </p>
          </section>

          <section id="terms-law">
            <p className="policy-page__section-number">07</p>
            <h2>Governing law, changes and contact</h2>
            <p>
              These terms are governed by the laws of Hong Kong, without
              depriving a consumer of mandatory protections in their country of
              residence. Courts with jurisdiction under applicable consumer law
              may hear a dispute. We encourage you to contact us first so we can
              try to resolve concerns directly.
            </p>
            <p>
              We may revise these terms for future use or orders. The terms
              shown when an order is placed continue to govern that purchase.
              Questions can be sent to{" "}
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>{" "}
              or through our{" "}
              <Link to="/contact" onClick={toTop}>
                contact page
              </Link>
              . {siteConfig.legalName} is registered at{" "}
              {siteConfig.contact.address}.
            </p>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <Link to="/privacy" onClick={toTop}>
              Privacy notice
            </Link>
            <Link to="/shipping" onClick={toTop}>
              Shipping
            </Link>
            <Link to="/returns" onClick={toTop}>
              Returns
            </Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Terms;
